import Cookies from 'js-cookie';
import { getQuery } from './querystring';

export const ATTRIBUTION_FIRST_COOKIE = 'tyw_attribution_first';
export const ATTRIBUTION_LAST_COOKIE = 'tyw_attribution_last';
/** First-touch: longer window for long sales cycles (pending product review vs 90). */
export const ATTRIBUTION_FIRST_COOKIE_DAYS = 180;
/** Last-touch: matches affiliate-code convention. */
export const ATTRIBUTION_LAST_COOKIE_DAYS = 90;

/** Ad-platform click cookies older than this are not paired with a cookie-only touch. */
export const CLICK_COOKIE_MAX_AGE_DAYS = 7;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'];
const DAY_MS = 24 * 60 * 60 * 1000;

const firstValue = value => {
  if (Array.isArray(value)) {
    return value[0] || null;
  }
  return value || null;
};

/**
 * Campaign hit if utm_source or any ad click id (gclid, gbraid, wbraid, fbclid) is present.
 */
export const isCampaignHit = (params = {}) =>
  Boolean(firstValue(params.utm_source) || CLICK_ID_KEYS.some(key => firstValue(params[key])));

/**
 * Build attribution object from query params, optionally folding in WP-set utm_* cookies.
 * Priority: query params on the SPA > existing utm_* cookies.
 */
export const buildAttributionObject = (queryParams = {}, cookieUtm = {}, cookieClickIds = {}) => {
  const params = {};

  UTM_KEYS.forEach(key => {
    params[key] = firstValue(queryParams[key]) || firstValue(cookieUtm[key]) || null;
  });

  CLICK_ID_KEYS.forEach(key => {
    params[key] = firstValue(queryParams[key]) || firstValue(cookieClickIds[key]) || null;
  });

  if (!isCampaignHit(params)) {
    return null;
  }

  return {
    utm_source: params.utm_source,
    utm_medium: params.utm_medium,
    utm_campaign: params.utm_campaign,
    utm_content: params.utm_content,
    utm_term: params.utm_term,
    gclid: params.gclid,
    gbraid: params.gbraid,
    wbraid: params.wbraid,
    fbclid: params.fbclid,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    landing_page: typeof window !== 'undefined' ? window.location.href : null,
    captured_at: new Date().toISOString()
  };
};

const readWpUtmCookies = () => ({
  utm_source: Cookies.get('utm_source') || null,
  utm_medium: Cookies.get('utm_medium') || null,
  utm_campaign: Cookies.get('utm_campaign') || null,
  utm_content: Cookies.get('utm_content') || null,
  utm_term: Cookies.get('utm_term') || null
});

/**
 * Click id from an ad-platform cookie, or null when missing, malformed or older than
 * CLICK_COOKIE_MAX_AGE_DAYS.
 * @param {string|undefined} raw
 * @param {number} prefixParts dot-separated parts before the click id
 * @param {number} msPerUnit milliseconds per unit of the cookie's timestamp
 * @param {number} now
 * @returns {string|null}
 */
const parseClickCookie = (raw, prefixParts, msPerUnit, now) => {
  if (!raw) {
    return null;
  }
  const parts = raw.split('.');
  const clickId = parts.slice(prefixParts).join('.');
  const clickedAt = Number(parts[prefixParts - 1]) * msPerUnit;
  if (!clickId || !clickedAt || now - clickedAt > CLICK_COOKIE_MAX_AGE_DAYS * DAY_MS) {
    return null;
  }
  return clickId;
};

/**
 * Click ids the Meta pixel (_fbc = fb.1.<ms>.<fbclid>) and the Google Ads conversion linker
 * (_gcl_aw = GCL.<s>.<gclid>, _gcl_gb = GCL.<s>.<gbraid>) keep after an ad click, for guests who
 * landed on the content site and reached the funnel without the click id in the URL.
 */
export const readClickIdCookies = (now = Date.now()) => ({
  fbclid: parseClickCookie(Cookies.get('_fbc'), 3, 1, now),
  gclid: parseClickCookie(Cookies.get('_gcl_aw'), 2, 1000, now),
  gbraid: parseClickCookie(Cookies.get('_gcl_gb'), 2, 1000, now)
});

const readTouch = name => {
  try {
    return JSON.parse(Cookies.get(name) || 'null');
  } catch {
    return null;
  }
};

const sameUtm = (a, b) => UTM_KEYS.every(key => (a[key] || null) === (b[key] || null));

/**
 * Capture first/last-touch attribution into cookies (call only after marketing consent).
 * First-touch is write-once; last-touch overwrites on every campaign hit in the URL.
 * Without campaign params in the URL, the WP utm_* cookies count as a touch only when they
 * differ from the stored last touch, so later page loads do not drop its click id.
 * No-op when this load is not a campaign hit (does not clobber existing cookies).
 */
export const captureAttribution = (location = window.location) => {
  const queryParams = getQuery(location);
  const hasQueryCampaign = isCampaignHit(queryParams);
  const cookieUtm = hasQueryCampaign ? {} : readWpUtmCookies();
  const cookieClickIds = hasQueryCampaign ? {} : readClickIdCookies();
  const attribution = buildAttributionObject(queryParams, cookieUtm, cookieClickIds);

  if (!attribution) {
    return null;
  }

  const lastTouch = readTouch(ATTRIBUTION_LAST_COOKIE);
  if (!hasQueryCampaign && lastTouch && sameUtm(lastTouch, attribution)) {
    return lastTouch;
  }

  const payload = JSON.stringify(attribution);

  if (!Cookies.get(ATTRIBUTION_FIRST_COOKIE)) {
    Cookies.set(ATTRIBUTION_FIRST_COOKIE, payload, { expires: ATTRIBUTION_FIRST_COOKIE_DAYS });
  }

  Cookies.set(ATTRIBUTION_LAST_COOKIE, payload, { expires: ATTRIBUTION_LAST_COOKIE_DAYS });

  return attribution;
};
